import { useCallback, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { useLoginWithUserCode } from '@/api/gen/user-code/user-code.gen';
import { ZepQuizApiError } from '@/api/httpClient';
import { useOnSuccessLogin } from '@/module/auth/login/hooks/useOnSuccessLogin';
import { CLASSROOM_ROUTES } from '@/shared/constants/routes';
import {
  type CodeLoginMethod,
  trackCodeLoginEvent,
} from '@/shared/features/code-login/utils';
import { buildAuthAppSigninUrl } from '@/shared/utils/auth';
import { getSafeRedirectUrl } from '@/utils/urlValidation';

import { parseScannedCode } from './features/qr-scan/parseScannedCode';

const INVALID_QR_TOAST_ID = 'code-login-invalid-qr';

/**
 * 검증 성공 시 이동할 목적지를 계산한다. 단일 학급이면 그 학급의 학생 탭으로 바로 보낸다.
 *
 * 퀴즈 탭으로 보내던 것을 학생 탭으로 바꿨다 — 코드로 들어온 학생이 아바타·포인트를
 * 어디서 보는지 인지하지 못한다는 제보가 반복됐고("처음 코드 타고 들어가면 학생탭이 아닌
 * 퀴즈 탭이 먼저 표시돼서 어디있는지 잠깐 헤맸어요"), owner·참여자의 일반 진입 경로인
 * `CLASSROOM_ROUTES.detail` 도 이미 학생 탭으로 통일돼 있어 진입 경로만 어긋나 있었다.
 */
function getLoginDestination(res: {
  hasJoinedSingleClassroom: boolean;
  classroomId?: string;
}): string {
  return res.hasJoinedSingleClassroom && res.classroomId
    ? CLASSROOM_ROUTES.studentList(res.classroomId)
    : CLASSROOM_ROUTES.list();
}

type CodeVerifyFailTrackingProps = {
  // 임시 진단용: 실패 입력 전체(평문). 학생이 무엇을 잘못 입력했는지(U 누락 / 클래스코드 /
  // 퀴즈 입장코드 오입력 등) 분류하기 위해 서버팀 합의 하에 한시적으로 평문 저장한다.
  // ⚠️ 학생코드는 credential 이므로 진단이 끝나면 파생 신호(has_u_prefix 등)로 교체/제거할 것.
  // 실패 케이스에만 기록하며 success_code_verify 에는 절대 넣지 않는다.
  input_code?: string;
  http_status?: number;
  api_status?: string;
};

const compactTrackingProps = (
  props: CodeVerifyFailTrackingProps,
): CodeVerifyFailTrackingProps =>
  Object.fromEntries(
    Object.entries(props).filter(([, value]) => value !== undefined),
  );

function getFailTrackingProps(
  error: unknown,
  code: string,
): CodeVerifyFailTrackingProps {
  const baseProps = {
    input_code: code,
  };

  if (error instanceof ZepQuizApiError) {
    return compactTrackingProps({
      ...baseProps,
      http_status: error.httpStatus,
      api_status: error.status,
    });
  }

  if (
    error !== null &&
    typeof error === 'object' &&
    'response' in error &&
    (error as { response: unknown }).response !== undefined &&
    (error as { response: unknown }).response !== null
  ) {
    const response = (
      error as {
        response: { status?: number; data?: { status?: string } };
      }
    ).response;

    return compactTrackingProps({
      ...baseProps,
      http_status: response.status,
      api_status: response.data?.status,
    });
  }

  return compactTrackingProps(baseProps);
}

export function useCodeLogin() {
  const { t } = useTranslation('classroom');
  const router = useRouter();
  const login = useLoginWithUserCode();
  const onSuccessLogin = useOnSuccessLogin();

  // 보호 페이지에서 튕겨온 경우 `?callbackUrl=...`로 원래 목적지가 실려온다. 일반 로그인과 동일하게
  // 검증 성공 후 그 위치로 복귀시킨다(없으면 login 결과 기반 기본 목적지). 최종 안전성 검증은
  // onSuccessLogin 의 getSafeRedirectUrl 이 담당.
  const callbackParam =
    typeof router.query.callbackUrl === 'string'
      ? router.query.callbackUrl
      : undefined;

  // 성공 시 true, 실패 시 false 를 반환한다. 딥링크 자동 검증(CodeLoginPageView)이
  // 성공이면 로딩 유지(리다이렉트까지), 실패면 폼 폴백으로 전환하는 데 사용한다.
  const handleVerify = useCallback(
    async (code: string, login_method: CodeLoginMethod): Promise<boolean> => {
      // 스캐너가 같은 코드를 매 프레임 디코드해도 검증 요청이 중복되지 않도록,
      // 이미 진행 중이면 무시한다.
      if (login.isPending) return false;
      trackCodeLoginEvent('submit_code_verify', { login_method });
      try {
        const { data } = await login.mutateAsync({ data: { userCode: code } });

        // onSuccessLogin 이 토큰 저장 + 유저 동기화 + 리다이렉트까지 직접 수행한다.
        // success_code_verify 는 onResolved 콜백 안에서만 발화(destination/is_account_switch 단일 진실원).
        await onSuccessLogin({
          zepServerToken: data.token,
          // sessionKey는 OpenAPI상 optional이나 BE processLoginSuccess가 항상 createSession으로 세팅하므로 빈 문자열은 런타임 미발생. `?? ''`는 string 필수 타입을 만족시키는 방어적 폴백.
          userSessionKey: data.sessionKey ?? '',
          callbackUrl: callbackParam ?? getLoginDestination(data),
          onResolved: ({ isAccountSwitch, destination }) => {
            trackCodeLoginEvent('success_code_verify', {
              login_method,
              classroom_id: data.classroomId,
              is_account_switch: isAccountSwitch,
              destination,
            });
          },
        });
        return true;
      } catch (error) {
        // C1: 코드 존재/오류 여부를 구분해 노출하지 않기 위해 모든 실패에 동일 문구를 쓴다.
        toast.error(t('codeLogin.entryFailed'));
        trackCodeLoginEvent('fail_code_verify', {
          login_method,
          ...getFailTrackingProps(error, code),
        });
        return false;
      }
    },
    [login, onSuccessLogin, callbackParam, t],
  );

  // 스캐너가 유효 QR을 매 프레임 디코드해 같은 코드로 login 이 반복 호출되면
  // isPending 토글로 입장 버튼이 깜빡인다. 한 번 보낸 코드는 다시 보내지 않는다.
  const lastScannedCodeRef = useRef<string | null>(null);
  // fail_qr_parse: 스캔 세션당 1회만 발화하도록 ref 가드한다.
  const parseFailLoggedRef = useRef(false);

  const handleScan = useCallback(
    (text: string) => {
      const code = parseScannedCode(text);
      if (!code) {
        // 카메라가 잘못된 QR을 매 프레임 디코드해도 토스트가 쌓이지 않도록 고정 id로
        // 하나만 갱신 노출한다(react-hot-toast 가 같은 id 면 새로 쌓지 않고 갱신).
        toast.error(t('codeLogin.invalidQr'), { id: INVALID_QR_TOAST_ID });
        // 스캔 세션 첫 파싱 실패만 기록(흔들림 연속 실패 중복 방지).
        if (!parseFailLoggedRef.current) {
          parseFailLoggedRef.current = true;
          trackCodeLoginEvent('fail_qr_parse', { login_method: 'scan' });
        }
        return;
      }
      // 유효 코드를 잡으면 잘못된 QR 토스트를 즉시 닫는다(매 프레임 갱신으로 타이머가
      // 리셋돼 성공 후에도 잔존하던 문제 방지).
      toast.dismiss(INVALID_QR_TOAST_ID);
      if (code === lastScannedCodeRef.current) return;
      lastScannedCodeRef.current = code;
      void handleVerify(code, 'scan').then(ok => {
        // 중복 요청 방지는 성공/진행 중일 때만 유지한다.
        // 실패 후 같은 QR을 다시 비추면 즉시 재시도할 수 있어야 한다.
        if (!ok && lastScannedCodeRef.current === code) {
          lastScannedCodeRef.current = null;
        }
      });
    },
    [handleVerify, t],
  );

  /** 카메라를 끄고 코드 입력 폼으로 전환할 때 parseFailLoggedRef 를 리셋한다.
   * 카메라를 다시 켜면(scanning true→false→true) 새 세션으로 간주한다. */
  const resetScanSession = useCallback(() => {
    parseFailLoggedRef.current = false;
    lastScannedCodeRef.current = null;
  }, []);

  // "이메일로 로그인" SSO(auth-app) 링크. callbackUrl 을 그대로 전파하면 open-redirect 가 되므로
  // (verify 경로는 onSuccessLogin 의 getSafeRedirectUrl 이 막지만 이 링크 경로는 검증을 안 거침)
  // 동일 도메인만 통과시키고 외부 URL 은 기본 목적지(/classrooms)로 폴백한다.
  const emailLoginUrl = buildAuthAppSigninUrl(
    router.locale ?? 'en',
    getSafeRedirectUrl(callbackParam, '/classrooms'),
  );

  return {
    handleVerify,
    handleScan,
    resetScanSession,
    isPending: login.isPending,
    emailLoginUrl,
  };
}
